/// Google Identity Services helper. Custom pixel buttons click a prepared
/// GIS control in the same user-gesture so the ID-token popup can open.

export class SocialSignInCancelled extends Error {
  constructor() {
    super('cancelled');
    this.name = 'SocialSignInCancelled';
  }
}

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  prompt: (listener?: (notification: {
    isNotDisplayed: () => boolean;
    isSkippedMoment: () => boolean;
    isDismissedMoment: () => boolean;
  }) => void) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

const gisSrc = 'https://accounts.google.com/gsi/client';

let scriptPromise: Promise<void> | null = null;
let preparedClientId = '';
let host: HTMLDivElement | null = null;
let pending: {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
} | null = null;

function loadGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${gisSrc}"]`);
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Sign-In')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = gisSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function settle(token: string): void {
  const waiter = pending;
  pending = null;
  waiter?.resolve(token);
}

function settleError(error: unknown): void {
  const waiter = pending;
  pending = null;
  waiter?.reject(error);
}

export async function prepareGoogleSignIn(clientId: string): Promise<void> {
  if (!clientId) return;
  await loadGis();
  const gis = window.google?.accounts.id;
  if (!gis) throw new Error('Google Sign-In is unavailable');

  if (!host) {
    host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.position = 'fixed';
    host.style.left = '-9999px';
    host.style.top = '0';
    document.body.appendChild(host);
  }

  if (preparedClientId === clientId && host.childElementCount > 0) return;

  gis.initialize({
    client_id: clientId,
    callback: (response) => {
      if (response.credential) settle(response.credential);
      else settleError(new Error('Google Sign-In returned no credential'));
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });
  host.replaceChildren();
  gis.renderButton(host, { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with' });
  preparedClientId = clientId;
}

/// Must run from a click handler so the GIS popup stays in the user gesture.
export async function requestGoogleIdToken(clientId: string): Promise<string> {
  await prepareGoogleSignIn(clientId);
  const gis = window.google?.accounts.id;
  if (!gis) throw new Error('Google Sign-In is unavailable');

  return new Promise((resolve, reject) => {
    pending = { resolve, reject };
    const button = host?.querySelector<HTMLElement>('div[role="button"]');
    if (button) {
      button.click();
      return;
    }
    gis.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment() || notification.isDismissedMoment()) {
        settleError(new SocialSignInCancelled());
      }
    });
  });
}
